import { APP_BASE_HREF, DATE_PIPE_DEFAULT_OPTIONS, PlatformLocation } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule, provideClientHydration, withNoHttpTransferCache } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools, StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ADDRESS_BOOK_CONFIG } from '@planone/bau365/data-access-address-book';
import { BUSINESS_CARD_SERVICE_CONFIG } from '@planone/bau365/data-access-business-card';
import { EXPORT_API_CONFIG, EXPORT_CONFIG } from '@planone/bau365/data-access-export';
import {
  Bau365DataAccessFairDataModule,
  FAIRS_CONFIGURATION,
  LANDINGPAGE_CONFIGURATION,
} from '@planone/bau365/data-access-fair-data';
import { NEWSFEED_SERVICE_CONFIG } from '@planone/bau365/data-access-newsfeed';
import { ONBOARDING_API_CONFIG } from '@planone/bau365/data-access-onboarding';
import { QR_CODE_VALIDATOR_CONFIG } from '@planone/bau365/data-access-qrcode-scanner';
import { SEARCH_SERVICE_CONFIG } from '@planone/bau365/data-access-search';
import { TICKET_SERVICE_CONFIG } from '@planone/bau365/data-access-tickets';
import { Bau365DataAccessTrackingModule } from '@planone/bau365/data-access-tracking';
import { USER_SERVICE_CONFIG, USER_SESSION_STATUS, WATCHLIST_CONFIG } from '@planone/bau365/data-access-user';
import { ServerErrorInterceptor } from '@planone/bau365/feature-errors';
import { SAS_SCRIPTS_LOADED, SAS_SCRIPTS_LOADING } from '@planone/bau365/feature-exhibitors';
import { Bau365FeatureShellModule } from '@planone/bau365/feature-shell';
import { Bau365FeatureTrackingModule } from '@planone/bau365/feature-tracking';
import { SELFSERVICE_CONFIG } from '@planone/bau365/ui-shell';
import { MOCK_API } from '@planone/bau365/util-api-response-mock-generators';
import { DEFAULT_FAIR_ID } from '@planone/bau365/util-fair-data';
import { BOT_REGEX } from '@planone/bau365/util-restricted-access';
import { EMPTY_SEARCH_REGEX } from '@planone/bau365/util-search';
import { USER_DATA_CONFIG } from '@planone/bau365/util-user-data';
import { SharedUtilFeatureFlagsModule, UNLEASH_CONFIGURATION } from '@planone/shared/data-access-feature-flags';
import { SALES_PRODUCTS_CONFIGURATION } from '@planone/shared/data-access-sales-products';
import { GOOGLE_ANALYTICS_CONFIGURATION } from '@planone/shared/feature-tracking-google-analytics';
import { SNOWPLOW_CONFIGURATION } from '@planone/shared/feature-tracking-snowplow';
import { SHARE_BUTTON_CONFIG } from '@planone/shared/ui-elements';
import { ICON_CONFIG } from '@planone/shared/ui-icons';
import {
  COOKIEHANDLING_CONFIG,
  COOKIEHANDLING_SERVICE,
  CookiehandlingService,
} from '@planone/shared/util-cookie-handling';
import { ANALYTICS_INTERCEPTOR_CONFIG } from '@planone/shared/util-interceptors';
import {
  AnalyticsExcludeInterceptor,
  BASIC_AUTH_INTERCEPTOR_CONFIG,
  BasicAuthInterceptor,
  LanguageInterceptor,
} from '@planone/shared/util-interceptors';
import { CustomErrorHandler, sentryBreadcrumb } from '@planone/shared/util-logging';
import { ConsoleLoggingService, LOGGING_SERVICE, SentryLoggingService } from '@planone/shared/util-logging';
import { PLATFORM_TITLE } from '@planone/shared/util-meta';
import { SharedUtilNgrxModule } from '@planone/shared/util-ngrx';
import { SharedUtilPipelinesModule } from '@planone/shared/util-pipelines';
import { EXCLUDE_ANALYTICS } from '@planone/shared/util-tracking';
import { KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular';
import { BehaviorSubject } from 'rxjs';

import { AppComponent } from './app.component';

import { environment } from '../environments/environment';
import { ENVIRONMENT } from '../environments/environment.interface';

export const metaReducers: MetaReducer[] = [sentryBreadcrumb];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    Bau365FeatureShellModule,
    Bau365DataAccessTrackingModule,
    BrowserAnimationsModule,
    StoreModule.forRoot({}, { metaReducers }),
    !environment.production
      ? [
          StoreDevtoolsModule.instrument({
            name: 'Bau365',
            maxAge: 50,
            connectInZone: true,
          }),
        ]
      : [],
    EffectsModule.forRoot([]),
    SharedUtilNgrxModule,
    HttpClientModule,
    SharedUtilPipelinesModule,
    Bau365DataAccessFairDataModule,
    Bau365FeatureTrackingModule,
    SharedUtilFeatureFlagsModule,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    KeycloakService,
    provideStore(
      {},
      {
        metaReducers: [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    !environment.production
      ? provideStoreDevtools({
          name: 'Bau365',
          maxAge: 50,
          connectInZone: true,
        })
      : [],
    provideClientHydration(withNoHttpTransferCache()),
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    {
      provide: DEFAULT_FAIR_ID,
      useValue: environment.defaultFairId,
    },
    {
      provide: EXPORT_API_CONFIG,
      useValue: environment.exportServiceUrl,
    },
    {
      provide: LOGGING_SERVICE,
      useFactory: () => {
        if (environment.sentry.environment !== 'dev') {
          return new SentryLoggingService({
            release: environment.sentry.release,
            dsn: environment.sentry.dsn,
            environment: environment.sentry.environment,
            tracingOrigins: environment.sentry.tracingOrigins,
            tracesSampleRate: environment.sentry.tracesSampleRate,
          });
        } else {
          return new ConsoleLoggingService();
        }
      },
    },
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: ONBOARDING_API_CONFIG,
      useValue: {
        fairDataGatewayUrl: environment.fairDataGatewayUrl,
        searchUrl: environment.searchUrl,
      },
    },
    {
      provide: SEARCH_SERVICE_CONFIG,
      useValue: {
        searchApiURL: environment.searchUrl,
        fairDataGatewayURL: environment.fairDataGatewayUrl,
        itemsPerPage: 48,
      },
    },
    {
      provide: NEWSFEED_SERVICE_CONFIG,
      useValue: {
        newsfeedApiUrl: environment.searchUrl,
        quicklinksApiUrl: environment.analyticsUrl,
        fairDataGatewayUrl: environment.fairDataGatewayUrl,
        userHistoryUrl: environment.userUrl,
        initialItemsCount: 48,
        loadMoreItemsCount: 48,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AnalyticsExcludeInterceptor,
      multi: true,
    },
    {
      provide: USER_SERVICE_CONFIG,
      useValue: {
        channelsApiUrl: environment.userUrl,
        keycloakApiURL: environment.keycloakUrl,
        userServiceApiURL: environment.userUrl,
        fairDataGatewayURL: environment.fairDataGatewayUrl,
      },
    },
    {
      provide: TICKET_SERVICE_CONFIG,
      useValue: {
        ticketServiceApiUrl: environment.ticketUrl,
      },
    },
    {
      provide: BUSINESS_CARD_SERVICE_CONFIG,
      useValue: { userServiceApiURL: environment.userUrl },
    },
    {
      provide: QR_CODE_VALIDATOR_CONFIG,
      useValue: {
        host: environment.host,
        userServiceApiUrl: environment.userUrl,
      },
    },
    {
      provide: ADDRESS_BOOK_CONFIG,
      useValue: {
        userServiceApiUrl: environment.userUrl,
      },
    },
    {
      provide: USER_DATA_CONFIG,
      useValue: { userServiceApiURL: environment.userUrl },
    },
    {
      provide: SALES_PRODUCTS_CONFIGURATION,
      useValue: { leadServiceUrl: environment.leadServiceUrl },
    },
    {
      provide: WATCHLIST_CONFIG,
      useValue: {
        pageSize: 12,
      },
    },
    {
      provide: USER_SESSION_STATUS,
      useValue: new BehaviorSubject(false),
    },
    {
      provide: COOKIEHANDLING_SERVICE,
      useClass: CookiehandlingService,
    },
    {
      provide: COOKIEHANDLING_CONFIG,
      useValue: {
        production: environment.production,
        defaultDomain: environment.host,
      },
    },
    {
      provide: ICON_CONFIG,
      useValue: {
        domain: environment.iconHost,
      },
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: SHARE_BUTTON_CONFIG,
      useValue: environment.host,
    },
    {
      provide: EXPORT_CONFIG,
      useValue: environment.host,
    },
    {
      provide: SELFSERVICE_CONFIG,
      useValue: environment.selfserviceHost,
    },
    {
      provide: BASIC_AUTH_INTERCEPTOR_CONFIG,
      useValue: {
        basic_auth: environment.basicAuth,
        urlPattern: '^assets/.*',
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BasicAuthInterceptor,
      multi: true,
    },
    {
      provide: ANALYTICS_INTERCEPTOR_CONFIG,
      useValue: {
        keycloakUrl: environment.keycloakUrl,
        fairDataGatewayUrl: environment.fairDataGatewayUrl,
      },
    },
    {
      provide: PLATFORM_TITLE,
      useValue: environment.platformTitle,
    },
    {
      provide: MOCK_API,
      useValue: environment.mockApiResponses,
    },
    {
      provide: SAS_SCRIPTS_LOADED,
      useValue: new BehaviorSubject(false),
    },
    {
      provide: SAS_SCRIPTS_LOADING,
      useValue: new BehaviorSubject(false),
    },
    {
      provide: EXCLUDE_ANALYTICS,
      useValue: new BehaviorSubject(false),
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        timezone: '+0200',
      },
    },
    {
      provide: SNOWPLOW_CONFIGURATION,
      useValue: {
        production: environment.production,
        anonymized: true,
        trackerConfig: environment.trackers.snowplow.trackerConfig,
        trackerId: environment.trackers.snowplow.trackerId,
        endpoint: environment.trackers.snowplow.endpoint,
        excludeSearchParams: ['filter'],
        excludeLinkUrls: [environment.keycloakUrl],
      },
    },
    {
      provide: GOOGLE_ANALYTICS_CONFIGURATION,
      useValue: {
        production: environment.production,
        propertyId: environment.trackers.googleAnalytics.property,
        excludeLinkUrls: [environment.keycloakUrl],
      },
    },
    {
      provide: FAIRS_CONFIGURATION,
      useValue: environment.fairsConfiguration,
    },
    {
      provide: LANDINGPAGE_CONFIGURATION,
      useValue: environment.landingpageConfiguration,
    },
    {
      provide: UNLEASH_CONFIGURATION,
      useValue: environment.unleash,
    },
    {
      provide: BOT_REGEX,
      useValue: environment.botRegex,
    },
    {
      provide: EMPTY_SEARCH_REGEX,
      useValue: environment.spaceRegex,
    },
  ],
})
export class AppBaseModule {}
